import React, { useEffect, useMemo, useRef } from 'react';
import { __t } from '../../../../../base/utils/t';
import { getRefById, RefMap, scrollToError } from '../../../../../base/utils';

import { CheckboxWithContent } from '../../../../../base/components/Forms/CheckboxWithContent';
import { Label } from '../../../../../base/components/Label';
import { HoverTooltip } from '../../../../../base/components/HoverTooltip';

import { ReactComponent as IndividualSearchIcon } from '../../../../assets/img/individual-search-icon.svg';
import { ReactComponent as AgencySearchIcon } from '../../../../assets/img/agency-search-icon.svg';

import { useAppDispatch, useAppSelector, useIsMobile } from '../../../../../base/hooks';
import { vacancyFormActions } from '../../redux/slices/vacancyFormSlice';
import { ErrorMessage } from '../../../../../base/components/Forms/ErrorMessage';
import { VacancyType } from '../../enums';

import './styles.scss';

export const Step1 = () => {
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();
    const {
        fields: { forRecruiters, forWorkers },
        errors,
        shouldScrollToError,
    } = useAppSelector((state) => state.vacancyForm);

    const refMap: RefMap[] = [
        {
            id: 'forWorkers',
            ref: useRef<HTMLDivElement>(null),
        },
    ];

    useEffect(() => {
        if (errors && shouldScrollToError) {
            scrollToError(errors, refMap);
            dispatch(vacancyFormActions.setShouldScrollToError(false));
        }
    }, [refMap, errors, shouldScrollToError]);

    const checkboxChangeHandler = (id: string, value: boolean) =>
        dispatch(vacancyFormActions.setField({ id, value, errorGroupName: 'forWorkers' }));

    const hasError = useMemo((): boolean => !!errors?.forWorkers && !!errors?.forWorkers.length, [errors]);

    return (
        <div
            className='vacancy-form vacancy-form-step-1 scroll-margin-for-fixed-header'
            ref={getRefById('forWorkers', refMap)?.ref}
        >
            <h5 className='vacancy-form-page__title'>{__t('Укажите, что вас интересует')}</h5>

            <div className='vacancy-form-step-1__checkbox-wrap'>
                <CheckboxWithContent
                    id={VacancyType.ForWorkers}
                    checked={forWorkers}
                    className='vacancy-form-step-1__checkbox-item'
                    hasError={hasError}
                    onChange={checkboxChangeHandler}
                >
                    <div className='vacancy-form-step-1__checkbox-content'>
                        <IndividualSearchIcon className='vacancy-form-step-1__checkbox-content-icon' />
                        <h6 className='vacancy-form-step-1__checkbox-content-title'>
                            {__t('Самостоятельный поиск работников')}
                        </h6>
                        <ul className='vacancy-form-step-1__checkbox-content-list'>
                            <li className='vacancy-form-step-1__checkbox-content-list-item'>
                                {__t('Вакансия будет доступна для работников')}
                            </li>
                            <li className='vacancy-form-step-1__checkbox-content-list-item'>
                                {__t('Получайте отклики на вакансию от работников')}
                            </li>
                            <li className='vacancy-form-step-1__checkbox-content-list-item'>
                                {__t('Связывайтесь и договаривайтесь с подходящими работниками')}
                            </li>
                        </ul>
                    </div>
                </CheckboxWithContent>

                <CheckboxWithContent
                    id={VacancyType.ForRecruiters}
                    checked={forRecruiters}
                    className='vacancy-form-step-1__checkbox-item'
                    hasError={hasError}
                    onChange={checkboxChangeHandler}
                >
                    <div className='vacancy-form-step-1__checkbox-content'>
                        <HoverTooltip
                            className='vacancy-form-step-1__label'
                            text={__t(
                                'Мы только открыли регистрацию для рекрутинговых агентств и рекрутеров со всего мира. Поэтому первое время количество обращений может быть низкое, но оно будет увеличиваться с каждым днем.',
                            )}
                            align={isMobile ? 'left' : 'center'}
                        >
                            <Label type='danger'>{__t('New')}</Label>
                        </HoverTooltip>

                        <AgencySearchIcon className='vacancy-form-step-1__checkbox-content-icon' />
                        <h6 className='vacancy-form-step-1__checkbox-content-title'>
                            {__t('Поиск рекрутингового агентства')}
                        </h6>
                        <ul className='vacancy-form-step-1__checkbox-content-list'>
                            <li className='vacancy-form-step-1__checkbox-content-list-item'>
                                {__t('Вакансия будет доступна для рекрутинговых агентств и частных рекрутеров')}
                            </li>
                            <li className='vacancy-form-step-1__checkbox-content-list-item'>
                                {__t('Получайте предложения от рекрутеров')}
                            </li>
                            <li className='vacancy-form-step-1__checkbox-content-list-item'>
                                {__t('Связывайтесь и обсуждайте условия сотрудничества')}
                            </li>
                        </ul>
                    </div>
                </CheckboxWithContent>

                <ErrorMessage errors={errors?.forWorkers} align='center' />
            </div>
        </div>
    );
};
